import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { AdminAccessGate } from "@/components/admin-access-gate";
import { GpsDriveSessionBanner } from "@/components/gpsdrive-session-banner";
import {
  useCommunicationFailureSettings,
  useTestSendCommunicationFailure,
  useUpdateCommunicationFailureSettings,
} from "@/hooks/queries";
import { GpsDriveApiError } from "@/lib/api/gpsdrive-client";

export const Route = createFileRoute("/admin/communication-failure")({
  component: () => (
    <AdminAccessGate action="comunicacao">
      <CommunicationFailurePage />
    </AdminAccessGate>
  ),
  head: () => ({ meta: [{ title: "Falha de Comunicação — Administração — Wp Rastreamento" }] }),
});

// Espelha COMMUNICATION_FAILURE_TEMPLATE_VARIABLES no backend
// (src/communication-failure/communication-failure-template.ts) — qualquer
// variável nova precisa ser adicionada nos dois lugares.
const TEMPLATE_VARIABLES: { key: string; label: string; example: string }[] = [
  { key: "cliente", label: "Nome do cliente", example: "Cliente Teste" },
  { key: "veiculo", label: "Veículo (modelo)", example: "Fiat Toro" },
  { key: "placa", label: "Placa", example: "ABC1D23" },
  { key: "horas", label: "Horas sem comunicação", example: "5" },
  { key: "data", label: "Data/hora da última comunicação", example: "10/09/2026 14:30" },
];

const DEFAULT_TEMPLATE =
  "⚠️ *Falha de comunicação*\n\n" +
  "O veículo {{veiculo}} placa *{{placa}}* está sem comunicação com o rastreador " +
  "há mais de {{horas}}h (última atualização: {{data}}).";

// Agenda de envio (pedido 2026-09-27). 0 = domingo, igual ao backend.
const WEEKDAYS: { value: number; short: string; long: string }[] = [
  { value: 1, short: "Seg", long: "segunda" },
  { value: 2, short: "Ter", long: "terça" },
  { value: 3, short: "Qua", long: "quarta" },
  { value: 4, short: "Qui", long: "quinta" },
  { value: 5, short: "Sex", long: "sexta" },
  { value: 6, short: "Sáb", long: "sábado" },
  { value: 0, short: "Dom", long: "domingo" },
];
const DAY_PRESETS: { label: string; days: number[] }[] = [
  { label: "Todos os dias", days: [0, 1, 2, 3, 4, 5, 6] },
  { label: "Segunda a sexta", days: [1, 2, 3, 4, 5] },
  { label: "3x por semana (seg/qua/sex)", days: [1, 3, 5] },
  { label: "2x por semana (ter/qui)", days: [2, 4] },
  { label: "1x por semana (segunda)", days: [1] },
];
// "0" = até o veículo voltar a comunicar.
const MAX_SENDS_OPTIONS = ["1", "2", "3", "5", "10", "0"];
const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

const sameDays = (a: number[], b: number[]) =>
  a.length === b.length && [...a].sort().every((d, i) => d === [...b].sort()[i]);

function describeSchedule(opts: {
  scheduleMode: "imediato" | "horarios";
  sendDays: number[];
  windowStart: string;
  windowEnd: string;
  sendTimes: string[];
  maxSends: string;
  repeatIntervalHours: string;
}): string {
  const preset = DAY_PRESETS.find((p) => sameDays(p.days, opts.sendDays));
  const days = preset
    ? preset.label.toLowerCase()
    : WEEKDAYS.filter((w) => opts.sendDays.includes(w.value))
        .map((w) => w.long)
        .join(", ");
  const when =
    opts.scheduleMode === "horarios"
      ? `às ${[...opts.sendTimes].sort().join(", ")}`
      : opts.windowStart === "00:00" && opts.windowEnd === "23:59"
        ? "a qualquer hora, assim que detectar"
        : `assim que detectar, entre ${opts.windowStart} e ${opts.windowEnd}`;
  const n = Number(opts.maxSends);
  const times =
    n === 1
      ? "Um único aviso por desconexão."
      : `${n === 0 ? "Repete até o veículo voltar a comunicar" : `Até ${n} avisos por desconexão`}${
          opts.scheduleMode === "horarios"
            ? " (um por horário)."
            : `, a cada ${opts.repeatIntervalHours}h no mínimo.`
        }`;
  return `Envia ${days}, ${when}. ${times}`;
}

function renderPreview(template: string): string {
  return TEMPLATE_VARIABLES.reduce(
    (text, v) => text.replaceAll(`{{${v.key}}}`, v.example),
    template,
  );
}

function CommunicationFailurePage() {
  const { data: settings, isLoading } = useCommunicationFailureSettings();
  const update = useUpdateCommunicationFailureSettings();
  const testSend = useTestSendCommunicationFailure();

  const [enabled, setEnabled] = useState(false);
  const [thresholdHours, setThresholdHours] = useState("24");
  const [messageTemplate, setMessageTemplate] = useState(DEFAULT_TEMPLATE);
  const [testPhone, setTestPhone] = useState("");
  const [scheduleMode, setScheduleMode] = useState<"imediato" | "horarios">("imediato");
  const [sendDays, setSendDays] = useState<number[]>([0, 1, 2, 3, 4, 5, 6]);
  const [windowStart, setWindowStart] = useState("00:00");
  const [windowEnd, setWindowEnd] = useState("23:59");
  const [sendTimes, setSendTimes] = useState<string[]>(["08:00"]);
  const [maxSends, setMaxSends] = useState("1");
  const [repeatIntervalHours, setRepeatIntervalHours] = useState("24");
  const [messageDelaySeconds, setMessageDelaySeconds] = useState("0");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!settings) return;
    setEnabled(settings.enabled);
    setThresholdHours(String(settings.thresholdHours));
    setMessageTemplate(settings.messageTemplate || DEFAULT_TEMPLATE);
    setScheduleMode(settings.scheduleMode ?? "imediato");
    setSendDays(settings.sendDays ?? [0, 1, 2, 3, 4, 5, 6]);
    setWindowStart(settings.windowStart ?? "00:00");
    setWindowEnd(settings.windowEnd ?? "23:59");
    setSendTimes(settings.sendTimes?.length ? settings.sendTimes : ["08:00"]);
    const max = String(settings.maxSends ?? 1);
    setMaxSends(MAX_SENDS_OPTIONS.includes(max) ? max : "1");
    setRepeatIntervalHours(String(settings.repeatIntervalHours ?? 24));
    setMessageDelaySeconds(String(settings.messageDelaySeconds ?? 0));
  }, [settings]);

  const toggleDay = (day: number) =>
    setSendDays((prev) => (prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]));

  const insertVariable = (key: string) => {
    const textarea = textareaRef.current;
    const token = `{{${key}}}`;
    if (!textarea) {
      setMessageTemplate((prev) => prev + token);
      return;
    }
    const start = textarea.selectionStart ?? messageTemplate.length;
    const end = textarea.selectionEnd ?? messageTemplate.length;
    const next = messageTemplate.slice(0, start) + token + messageTemplate.slice(end);
    setMessageTemplate(next);
    requestAnimationFrame(() => {
      textarea.focus();
      const cursor = start + token.length;
      textarea.setSelectionRange(cursor, cursor);
    });
  };

  const handleSave = async () => {
    const hours = Number(thresholdHours);
    if (!Number.isInteger(hours) || hours < 1) {
      toast.error("Informe um número inteiro de horas maior que zero");
      return;
    }
    if (!messageTemplate.trim()) {
      toast.error("Informe o texto do aviso");
      return;
    }
    if (sendDays.length === 0) {
      toast.error("Escolha pelo menos um dia da semana para enviar");
      return;
    }
    const times = sendTimes.map((t) => t.trim()).filter(Boolean);
    if (scheduleMode === "horarios" && (times.length === 0 || !times.every((t) => HHMM.test(t)))) {
      toast.error("Informe pelo menos um horário de envio válido");
      return;
    }
    if (scheduleMode === "imediato" && (!HHMM.test(windowStart) || !HHMM.test(windowEnd))) {
      toast.error("Informe a janela de horário de envio");
      return;
    }
    const interval = Number(repeatIntervalHours);
    if (!Number.isInteger(interval) || interval < 1 || interval > 720) {
      toast.error("O intervalo entre avisos deve ser de 1 a 720 horas");
      return;
    }
    const delay = Number(messageDelaySeconds);
    if (!Number.isInteger(delay) || delay < 0 || delay > 600) {
      toast.error("A pausa entre mensagens deve ser de 0 a 600 segundos");
      return;
    }
    try {
      await update.mutateAsync({
        enabled,
        thresholdHours: hours,
        messageTemplate: messageTemplate.trim(),
        scheduleMode,
        sendDays,
        windowStart,
        windowEnd,
        sendTimes: times.length ? times : ["08:00"],
        maxSends: Number(maxSends),
        repeatIntervalHours: interval,
        messageDelaySeconds: delay,
      });
      toast.success("Configuração de Falha de Comunicação salva");
    } catch (err) {
      toast.error(err instanceof GpsDriveApiError ? err.message : "Falha ao salvar a configuração");
    }
  };

  const handleTestSend = async () => {
    if (!testPhone.trim()) {
      toast.error("Informe um número de WhatsApp para o teste");
      return;
    }
    try {
      await testSend.mutateAsync({ phone: testPhone.trim(), messageTemplate: messageTemplate.trim() || undefined });
      toast.success("Mensagem de teste enviada");
    } catch (err) {
      toast.error(err instanceof GpsDriveApiError ? err.message : "Falha ao enviar o teste");
    }
  };

  return (
    <div className="flex h-full flex-col gap-4 p-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Falha de Comunicação</h1>
        <p className="text-sm text-muted-foreground">
          Avisa por WhatsApp o cliente vinculado ao veículo quando ele fica tempo demais sem
          comunicação com o rastreador.
        </p>
      </div>

      <GpsDriveSessionBanner />

      <Card className="max-w-xl rounded-2xl shadow-sm">
        <CardHeader>
          <CardTitle className="text-base">Configuração</CardTitle>
          <CardDescription>
            Limite único, aplicado a todos os veículos da empresa. Requer o WhatsApp conectado em
            Administração &gt; Conta &amp; Integrações &gt; Integrações.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Avisar cliente sobre falha de comunicação</p>
              <p className="text-xs text-muted-foreground">
                Desligar não apaga o limite configurado, só para de enviar os avisos.
              </p>
            </div>
            <Switch checked={enabled} onCheckedChange={setEnabled} disabled={isLoading} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="cf-threshold-hours">Avisar após quantas horas offline</Label>
            <Input
              id="cf-threshold-hours"
              type="number"
              min={1}
              max={720}
              value={thresholdHours}
              onChange={(e) => setThresholdHours(e.target.value)}
              disabled={isLoading}
              className="max-w-[160px]"
            />
            <p className="text-xs text-muted-foreground">
              Exemplo: 24 considera falha quando o veículo completa 1 dia sem comunicação. Quando e
              quantas vezes o cliente é avisado é definido na Agenda de envio abaixo.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className="max-w-xl rounded-2xl shadow-sm">
        <CardHeader>
          <CardTitle className="text-base">Agenda de envio</CardTitle>
          <CardDescription>
            Em quais dias e horários os avisos saem, quantas vezes repetir e a pausa entre uma
            mensagem e outra. Horário de Brasília.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label>Dias da semana</Label>
            <div className="flex flex-wrap gap-1.5">
              {DAY_PRESETS.map((p) => (
                <Button
                  key={p.label}
                  type="button"
                  size="sm"
                  variant={sameDays(p.days, sendDays) ? "default" : "outline"}
                  className="h-7 rounded-full px-3 text-xs"
                  onClick={() => setSendDays(p.days)}
                  disabled={isLoading}
                >
                  {p.label}
                </Button>
              ))}
            </div>
            <div className="flex flex-wrap gap-1.5 pt-1">
              {WEEKDAYS.map((w) => {
                const on = sendDays.includes(w.value);
                return (
                  <button
                    key={w.value}
                    type="button"
                    onClick={() => toggleDay(w.value)}
                    disabled={isLoading}
                    className={cn(
                      "h-9 w-12 rounded-md border text-sm font-medium transition-colors",
                      on
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border bg-background text-muted-foreground hover:bg-muted",
                    )}
                  >
                    {w.short}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground">
              Escolha um atalho ou marque os dias que quiser (ex.: só segunda e quinta).
            </p>
          </div>

          <div className="space-y-2">
            <Label>Quando enviar</Label>
            <RadioGroup
              value={scheduleMode}
              onValueChange={(v) => setScheduleMode(v as "imediato" | "horarios")}
              className="gap-2"
            >
              {(
                [
                  {
                    value: "imediato",
                    label: "Assim que detectar a falha",
                    hint: "Dentro de uma faixa de horário permitida",
                  },
                  {
                    value: "horarios",
                    label: "Em horários fixos",
                    hint: "Junta tudo e envia só nos horários escolhidos (ex.: 08:00 e 18:00)",
                  },
                ] as const
              ).map((o) => (
                <label
                  key={o.value}
                  className={cn(
                    "flex cursor-pointer items-start gap-3 rounded-lg border p-3",
                    scheduleMode === o.value ? "border-primary bg-primary/5" : "border-border",
                  )}
                >
                  <RadioGroupItem value={o.value} className="mt-0.5" />
                  <span>
                    <span className="block text-sm font-medium">{o.label}</span>
                    <span className="block text-xs text-muted-foreground">{o.hint}</span>
                  </span>
                </label>
              ))}
            </RadioGroup>

            {scheduleMode === "imediato" ? (
              <div className="flex flex-wrap items-center gap-2 pt-1 text-sm">
                <span className="text-muted-foreground">Enviar das</span>
                <Input
                  type="time"
                  value={windowStart}
                  onChange={(e) => setWindowStart(e.target.value)}
                  className="w-[120px]"
                />
                <span className="text-muted-foreground">às</span>
                <Input
                  type="time"
                  value={windowEnd}
                  onChange={(e) => setWindowEnd(e.target.value)}
                  className="w-[120px]"
                />
              </div>
            ) : (
              <div className="space-y-2 pt-1">
                {sendTimes.map((t, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input
                      type="time"
                      value={t}
                      onChange={(e) =>
                        setSendTimes((prev) => prev.map((x, j) => (j === i ? e.target.value : x)))
                      }
                      className="w-[120px]"
                    />
                    {sendTimes.length > 1 && (
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={() => setSendTimes((prev) => prev.filter((_, j) => j !== i))}
                        aria-label="Remover horário"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
                {sendTimes.length < 24 && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setSendTimes((prev) => [...prev, "18:00"])}
                  >
                    <Plus className="mr-1 h-4 w-4" />
                    Adicionar horário
                  </Button>
                )}
              </div>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Quantas vezes avisar</Label>
              <Select value={maxSends} onValueChange={setMaxSends}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MAX_SENDS_OPTIONS.map((o) => (
                    <SelectItem key={o} value={o}>
                      {o === "0" ? "Até voltar a comunicar" : o === "1" ? "Só 1 vez" : `${o} vezes`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Por desconexão de cada veículo.</p>
            </div>

            {scheduleMode === "imediato" && maxSends !== "1" && (
              <div className="space-y-2">
                <Label htmlFor="cf-repeat-interval">Intervalo entre avisos (horas)</Label>
                <Input
                  id="cf-repeat-interval"
                  type="number"
                  min={1}
                  max={720}
                  value={repeatIntervalHours}
                  onChange={(e) => setRepeatIntervalHours(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Tempo mínimo até repetir o aviso do mesmo veículo.
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="cf-message-delay">Pausa entre mensagens (segundos)</Label>
              <Input
                id="cf-message-delay"
                type="number"
                min={0}
                max={600}
                value={messageDelaySeconds}
                onChange={(e) => setMessageDelaySeconds(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Espera entre um WhatsApp e o próximo quando vários saem juntos. Ajuda a evitar
                bloqueio do número.
              </p>
            </div>
          </div>

          <div className="rounded-md border bg-muted/40 p-3 text-sm">
            {sendDays.length === 0
              ? "Nenhum dia selecionado — nenhum aviso será enviado."
              : describeSchedule({
                  scheduleMode,
                  sendDays,
                  windowStart,
                  windowEnd,
                  sendTimes: sendTimes.filter((t) => HHMM.test(t)),
                  maxSends,
                  repeatIntervalHours,
                })}
          </div>
        </CardContent>
      </Card>

      <Card className="max-w-xl rounded-2xl shadow-sm">
        <CardHeader>
          <CardTitle className="text-base">Texto do aviso</CardTitle>
          <CardDescription>
            Clique em uma variável para inserir no texto. Elas são substituídas pelos dados reais
            do veículo/cliente na hora do envio.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {TEMPLATE_VARIABLES.map((v) => (
              <Badge
                key={v.key}
                variant="secondary"
                className="cursor-pointer select-none hover:bg-secondary/70"
                onClick={() => insertVariable(v.key)}
                title={v.label}
              >
                {`{{${v.key}}}`}
              </Badge>
            ))}
          </div>

          <div className="space-y-2">
            <Label htmlFor="cf-message-template">Mensagem enviada ao cliente</Label>
            <Textarea
              id="cf-message-template"
              ref={textareaRef}
              value={messageTemplate}
              onChange={(e) => setMessageTemplate(e.target.value)}
              disabled={isLoading}
              rows={6}
              className="font-mono text-xs"
            />
          </div>

          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Pré-visualização (com dados de exemplo)</Label>
            <div className="whitespace-pre-wrap rounded-md border bg-muted/40 p-3 text-sm">
              {renderPreview(messageTemplate) || "—"}
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <Button onClick={handleSave} disabled={update.isPending || isLoading}>
              {update.isPending ? "Salvando…" : "Salvar"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="max-w-xl rounded-2xl shadow-sm">
        <CardHeader>
          <CardTitle className="text-base">Testar envio</CardTitle>
          <CardDescription>
            Envia o texto acima (mesmo que ainda não salvo) com dados de exemplo para um número de
            WhatsApp à sua escolha.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="cf-test-phone">Número de WhatsApp para teste</Label>
            <Input
              id="cf-test-phone"
              type="tel"
              placeholder="(11) 99999-9999"
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              className="max-w-[220px]"
            />
          </div>
          <div className="flex justify-end">
            <Button variant="outline" onClick={handleTestSend} disabled={testSend.isPending}>
              {testSend.isPending ? "Enviando…" : "Enviar teste"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
